<script setup lang="ts">
  import { defineProps, ref, onMounted, onUnmounted } from 'vue';
  import ButtonAtom from '@/components/atoms/ButtonAtom.vue';
  import IconAtom from '@/components/atoms/IconAtom.vue';

  const props = defineProps<{
    previewData: {
      resource_name: string;
      url_path: string | File;
      tags: { id: string; value: string }[];
    };
    coverImage: string;
  }>();

  const pdfContainer = ref<HTMLDivElement | null>(null);
  const currentPage = ref(1);
  const totalPages = ref(0);
  const isLoading = ref(true);
  const pdfError = ref(false);
  const pdfUrl = ref<string>('');

  onMounted(async () => {
    if (props.previewData.url_path) {
      await loadPdf();
    }
  });

  onUnmounted(() => {
    if (pdfUrl.value) {
      URL.revokeObjectURL(pdfUrl.value);
    }
  });

  const loadPdf = async () => {
    try {
      isLoading.value = true;
      pdfError.value = false;

      if (typeof props.previewData.url_path === 'object') {
        // It's a file object
        pdfUrl.value = URL.createObjectURL(props.previewData.url_path);
      } else {
        // It's a URL string
        pdfUrl.value = props.previewData.url_path;
      }

      // For preview, we'll show a placeholder since PDF.js requires additional setup
      // In production, you would integrate PDF.js here
      totalPages.value = 1; // Placeholder
      isLoading.value = false;
    } catch (error) {
      console.error('Error loading PDF:', error);
      pdfError.value = true;
      isLoading.value = false;
    }
  };

  const nextPage = () => {
    if (currentPage.value < totalPages.value) {
      currentPage.value++;
    }
  };

  const prevPage = () => {
    if (currentPage.value > 1) {
      currentPage.value--;
    }
  };
</script>

<template>
  <div>
    <h3 class="preview-title">E-book Preview</h3>
    <div class="pdf-preview-molecule">
      <div
        class="preview-header"
        :style="{
          backgroundImage: `url(${props.coverImage})`,
        }"
      >
        <h1>{{ props.previewData.resource_name }}</h1>
        <div class="tags-container">
          <ButtonAtom
            v-for="tag in props.previewData.tags"
            :key="tag.id"
            class="btn btn-secondary tag-button"
            :disabled="false"
          >
            {{ tag.value }}
          </ButtonAtom>
          <!-- E-book indicator tag -->
          <ButtonAtom class="btn btn-primary ebook-tag" :disabled="false">
            📚 E-book
          </ButtonAtom>
        </div>
      </div>

      <div class="pdf-content">
        <div v-if="isLoading" class="pdf-loading">
          <p>Loading PDF...</p>
        </div>

        <div v-else-if="pdfError" class="pdf-error">
          <IconAtom icon="closeIcon" />
          <p>Error loading PDF file</p>
        </div>

        <div v-else-if="!props.previewData.url_path" class="pdf-placeholder">
          <IconAtom icon="bookIcon" />
          <p>Upload a PDF file to see preview</p>
        </div>

        <div v-else class="pdf-viewer">
          <!-- PDF Viewer Placeholder -->
          <div class="pdf-viewer-placeholder">
            <IconAtom icon="bookIcon" />
            <p class="pdf-filename">
              {{
                typeof props.previewData.url_path === 'object'
                  ? props.previewData.url_path.name
                  : 'PDF Document'
              }}
            </p>
            <p class="pdf-info">PDF viewer will display here</p>
            <p class="pdf-note">
              <strong>Note:</strong> Full PDF viewer with pagination will be
              implemented in the app
            </p>
          </div>

          <!-- PDF Controls -->
          <div class="pdf-controls">
            <ButtonAtom
              class="btn btn-secondary"
              @click="prevPage"
              :disabled="currentPage <= 1"
            >
              <IconAtom icon="angleLeft" />
              Previous
            </ButtonAtom>

            <span class="page-info">
              Page {{ currentPage }} of {{ totalPages }}
            </span>

            <ButtonAtom
              class="btn btn-secondary"
              @click="nextPage"
              :disabled="currentPage >= totalPages"
            >
              Next
              <IconAtom icon="angleRight" />
            </ButtonAtom>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .preview-title {
    text-align: left;
    margin-bottom: 28px;
    font-weight: bold;
    color: var(--color-black);
  }

  .pdf-preview-molecule {
    display: flex;
    flex-direction: column;
    width: 90%;
    max-width: 800px;
    min-height: 600px;
    margin: auto;
    border-radius: 12px;
    background-color: #fff;
    border: 1px solid var(--color-black);
    padding: 0;
    box-sizing: border-box;
  }

  .preview-header {
    background-size: cover;
    background-position: center;
    font-weight: bold;
    background-color: var(--color-gray-30);
    padding: 20px;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    min-height: 240px;
    word-wrap: break-word;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .preview-header h1 {
    margin: 0;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
    line-height: 1.2;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    color: white;
  }

  .pdf-content {
    padding: 20px;
    overflow-y: auto;
    overflow-x: hidden;
    color: var(--color-black);
    border-top: 1px solid var(--color-black);
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    word-wrap: break-word;
    word-break: break-word;
    white-space: pre-wrap;
    text-align: center;
    max-width: 100%;
    box-sizing: border-box;
    min-height: 300px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .tags-container {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
  }

  .tag-button {
    color: #fff;
    border: 1px solid #fff;
    cursor: default;
  }

  .ebook-tag {
    background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
    border: 1px solid #fff;
    color: #fff;
    font-weight: bold;
  }

  .pdf-loading,
  .pdf-error,
  .pdf-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 15px;
    min-height: 200px;
    color: var(--color-gray-60);
  }

  .pdf-viewer {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .pdf-viewer-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-height: 200px;
    padding: 30px;
    border: 2px dashed var(--color-gray-30);
    border-radius: 8px;
    background: var(--color-gray-10);
  }

  .pdf-filename {
    font-weight: bold;
    font-size: 16px;
    color: var(--color-black);
  }

  .pdf-info {
    color: var(--color-gray-60);
    margin: 5px 0;
  }

  .pdf-note {
    font-size: 12px;
    color: var(--color-gray-60);
    text-align: center;
    max-width: 300px;
  }

  .pdf-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-top: 1px solid var(--color-gray-30);
    background: var(--color-gray-10);
    border-radius: 8px;
  }

  .page-info {
    font-weight: 500;
    color: var(--color-black);
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
